import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {
  add,
  eachDayOfInterval,
  endOfWeek,
  format,
  getDaysInMonth,
  lastDayOfMonth,
  startOfMonth,
  startOfWeek,
  sub,
} from 'date-fns'
import chunk from 'lodash-es/chunk'
import {type ReactNode, useId, useMemo} from 'react'

import type {DayNumber, DayOfWeek} from '../types'
import {Day, WeekdayHeaderDay} from './Day'
import styles from './Month.module.css'
import {useDatePickerContext} from './Provider'

const weekdayEnum: Record<DayOfWeek, DayNumber> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
}

const rowCount = 6

export interface MonthProps {
  date: Date
  'aria-describedby': string
}

export const Month = ({date, 'aria-describedby': descriptionId}: MonthProps) => {
  const {
    configuration: {weekStartsOn, compressedHeader},
  } = useDatePickerContext()
  const title = useMemo(() => format(date, 'MMMM yyyy'), [date])

  const weekdayHeaders = useMemo(() => {
    const now = new Date(date)
    const weekOptions: {weekStartsOn: DayNumber} = {
      weekStartsOn: weekdayEnum[weekStartsOn],
    }

    return eachDayOfInterval({start: startOfWeek(now, weekOptions), end: endOfWeek(now, weekOptions)}).map(d => (
      <WeekdayHeaderDay key={`weekday-${d.toISOString()}-header`} date={d} />
    ))
  }, [weekStartsOn, date])

  const dayComponents = useMemo(() => {
    const components: ReactNode[] = []
    const firstDay = startOfMonth(date)

    // Add preceding days from previous month
    const preBlanks = (firstDay.getDay() + (7 - weekdayEnum[weekStartsOn])) % 7
    const prevMonthLastDay = sub(firstDay, {days: 1})
    for (let i = 0; i < preBlanks; i++) {
      const day = sub(prevMonthLastDay, {days: preBlanks - i - 1})
      components.push(<Day key={`day-component-${day.toString()}`} date={day} parentMonth={date} />)
    }

    for (let i = 1; i <= getDaysInMonth(firstDay); i++) {
      const day = new Date(date.getFullYear(), date.getMonth(), i)
      components.push(<Day key={`day-component-${day.toString()}`} date={day} parentMonth={date} />)
    }

    let lastDay = lastDayOfMonth(firstDay)
    // Add trailing days from next month + fill out remaining empty row(s)
    while (components.length < rowCount * 7) {
      lastDay = add(lastDay, {days: 1})
      components.push(<Day key={`day-component-${lastDay.toString()}`} date={lastDay} parentMonth={date} />)
    }

    const rows = chunk(components, 7).map((days, index) => (
      // eslint-disable-next-line @eslint-react/no-array-index-key
      <div className={styles.row} key={`month-week-${index}`} role="row" aria-label={`Week ${index + 1}`}>
        {days}
      </div>
    ))
    return rows
  }, [weekStartsOn, date])

  const labelId = useId()

  return (
    <div
      className={styles.container}
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      role="dialog"
      {...testIdProps('month')}
    >
      <span className={clsx(styles.title, compressedHeader && styles.compressedHeader)} aria-live="polite" id={labelId}>
        {title}
      </span>
      <div role="grid" className={styles.grid}>
        {/* Column headers are hidden because days are labeled with the week day name */}
        <div className={styles.row} role="row" aria-hidden>
          {weekdayHeaders}
        </div>
        {dayComponents}
      </div>
    </div>
  )
}
